/**
 * VideoOutputPicker — chooser dropdown for picking a rendered video_projects
 * output and (optionally) publishing it as the OFFICIAL recording on a host
 * record (sermon, event).
 *
 * Two modes:
 *   1. Picker-only (default) — `onPick(url)` callback fires with the
 *      download URL of the chosen output. Caller writes it into their own
 *      form field. Used inside SermonDialog where there's already a
 *      `video_url` text field.
 *   2. Publish mode — when `module` + `hostId` props are set, picking an
 *      output also POSTs to /api/video/projects/{id}/output/{outputId}/publish
 *      which writes the URL into the host doc on the backend and stamps the
 *      audit trail. Used when the caller doesn't have a form already.
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Film, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

export default function VideoOutputPicker({
  tenantId,
  label = 'Pick from Video Library',
  module,
  hostId,
  field,
  onPick,
  onPublished,
  buttonVariant = 'outline',
  buttonSize = 'sm',
  testIdPrefix = 'video-output-picker',
}) {
  const [open, setOpen] = useState(false);

  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['video-projects-picker', tenantId],
    queryFn: () => api.get('/video/projects', { params: { tenant_id: tenantId } }).then(r => r.data),
    enabled: !!tenantId && open,
  });

  const publishMut = useMutation({
    mutationFn: ({ projectId, outputId }) =>
      api.post(`/video/projects/${projectId}/output/${outputId}/publish`, {
        module, host_id: hostId, field,
      }).then(r => r.data),
    onSuccess: (d) => {
      toast.success('Attached as official recording');
      setOpen(false);
      if (onPublished) onPublished(d);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Flatten projects → outputs[] so we can render a single grid.
  const projects = (projectsData?.projects || []).filter(p => (p.output_files || []).length > 0);
  const totalOutputs = projects.reduce((acc, p) => acc + (p.output_files?.length || 0), 0);

  const handleChoose = (project, output) => {
    const url = `/api/video/projects/${project.id}/output/${output.id}/download`;
    if (module && hostId) {
      publishMut.mutate({ projectId: project.id, outputId: output.id });
    } else if (onPick) {
      onPick(url, { project, output });
      setOpen(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={buttonVariant} size={buttonSize} data-testid={`${testIdPrefix}-open-btn`}>
          <Film className="h-4 w-4 mr-1" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>Choose a rendered output</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : totalOutputs === 0 ? (
          <p className="text-sm text-slate-500 text-center py-12 border border-dashed rounded" data-testid={`${testIdPrefix}-empty`}>
            No rendered outputs in this tenant's Video Library yet. Open the Studio Video Editor to render a clip first.
          </p>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-auto" data-testid={`${testIdPrefix}-list`}>
            {projects.map(p => (
              <div key={p.id}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  {p.title} <Badge variant="outline" className="ml-1 text-[10px]">{p.status}</Badge>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(p.output_files || []).map(o => (
                    <Card key={o.id} className="cursor-pointer hover:border-fuchsia-300 transition" onClick={() => handleChoose(p, o)} data-testid={`${testIdPrefix}-item-${o.id}`}>
                      <CardContent className="p-3 flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{o.label || o.filename}</p>
                          <p className="text-xs text-slate-500">
                            {o.social_format || ''} · {Math.round((o.size_bytes || 0) / 1024 / 1024)} MB
                          </p>
                        </div>
                        {publishMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500 opacity-0 hover:opacity-100" />}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
